import { createApiCall, resolveOnStatus } from './index';
import type { InstallationStatus, InstallationType, InstallationPlatform } from '@repo/types';

export interface Installation {
  id: string;
  installation_url: string;
  domain: string | null;
  status: InstallationStatus;
  installation_type: InstallationType;
  is_primary: boolean;
  platform: InstallationPlatform | null;
  company_settings_id: string;
  created_at: string;
  updated_at: string | null;
}

export interface GetPrimaryInstallationResponse {
  installation: Installation;
}

export interface CreateManualInstallationRequest {
  installation_url: string;
}

export interface CreateManualInstallationResponse {
  installation: Installation;
}

export interface UpdateInstallationRequest {
  installation_url: string;
}

export interface UpdateInstallationResponse {
  installation: Installation;
}

export interface SetAsPrimaryRequest {
  installation_id: string;
}

export interface SetAsPrimaryResponse {
  installation: Installation;
}

/**
 * Why verification failed, as a code the UI branches on for guidance + severity.
 * `http_*` carries the probed site's status (`http_404`, `http_403`), so this is
 * intentionally open-ended. Null/absent when verified — and absent, rather than
 * null, on a client-side catch where no server verdict was ever received.
 */
export type VerifyInstallationErrorCode =
  | 'invalid_url'
  | 'snippet_missing'
  | 'timeout'
  | 'unreachable'
  | (string & {});

export interface VerifyInstallationResult {
  verified: boolean;
  checked_url: string;
  status_code: number | null;
  message: string;
  error_code?: VerifyInstallationErrorCode | null;
}

export const installationApi = {
  // The three reads below all treat a 404 as "not configured yet" — an expected empty state,
  // not a failure. That has to be expressed by CATCHING the 404 (`resolveOnStatus`) rather
  // than by reading it off a resolved response: `createApiCall` rejects on every >= 400
  // (VRG-386), so the old `if (response.status === 404) return null` line was unreachable and
  // the 404 escaped as a query error. Since `useInstallations` sets `retry: false`, that put
  // the InstallationPage into a hard error state for every company that has not installed the
  // widget yet — i.e. the default state — which is the exact "stray Not found" regression the
  // original comment here says this branch exists to prevent.
  async getPrimaryInstallation(): Promise<Installation | null> {
    return resolveOnStatus(
      createApiCall({
        method: 'GET',
        url: '/installations/admin/primary',
      }).then((response) => response.data),
      [404],
      null
    );
  },

  async getDefaultInstallation(): Promise<Installation | null> {
    return resolveOnStatus(
      createApiCall({
        method: 'GET',
        url: '/installations/admin/default',
      }).then((response) => response.data),
      [404],
      null
    );
  },

  async getCurrentInstallation(): Promise<Installation | null> {
    return resolveOnStatus(
      createApiCall({
        method: 'GET',
        url: '/installations/admin',
      }).then((response) => response.data),
      [404],
      null
    );
  },

  async createManualInstallation(request: CreateManualInstallationRequest): Promise<Installation> {
    const response = await createApiCall({
      method: 'POST',
      url: '/installations/admin',
      data: request,
    });

    if (response.status !== 201) {
      throw new Error(response.data?.message || 'Failed to create installation');
    }

    return response.data;
  },

  async updateInstallation(request: UpdateInstallationRequest): Promise<Installation> {
    const response = await createApiCall({
      method: 'PUT',
      url: '/installations/admin',
      data: request,
    });

    if (response.status !== 200) {
      throw new Error(response.data?.message || 'Failed to update installation');
    }

    return response.data;
  },

  async deleteInstallation(installationId: string): Promise<void> {
    const response = await createApiCall({
      method: 'DELETE',
      url: `/installations/admin/${installationId}`,
    });

    if (response.status !== 204) {
      throw new Error(response.data?.message || 'Failed to delete installation');
    }
  },

  async setAsPrimary(installationId: string): Promise<Installation> {
    const response = await createApiCall({
      method: 'PUT',
      url: `/installations/admin/${installationId}/set-primary`,
    });

    if (response.status !== 200) {
      throw new Error(response.data?.message || 'Failed to set installation as primary');
    }

    return response.data;
  },

  async verifyInstallation(): Promise<VerifyInstallationResult> {
    const response = await createApiCall({
      method: 'POST',
      url: '/installations/admin/verify',
    });

    if (response.status !== 200 && response.status !== 201) {
      throw new Error(response.data?.message || 'Failed to verify installation');
    }

    return response.data;
  },

  /**
   * VRG-560: email the rendered widget snippet to an arbitrary address.
   *
   * No `invalidateQueries` follows this anywhere — a send changes no server
   * state that any query reads, so refetching the installation list afterwards
   * would be pure noise.
   */
  async sendInstallInstructions(request: { to: string }): Promise<{ sent: boolean }> {
    const response = await createApiCall({
      method: 'POST',
      url: '/installations/admin/send-instructions',
      data: request,
    });

    if (response.status !== 200 && response.status !== 201) {
      throw new Error(response.data?.message || 'Failed to send install instructions');
    }

    return response.data;
  },

  async getWidgetCode(): Promise<{ code: string; base_url: string }> {
    const response = await createApiCall({
      method: 'GET',
      url: '/installations/admin/widget-code',
    });

    if (response.status !== 200) {
      throw new Error(response.data?.message || 'Failed to get widget code');
    }

    return response.data;
  },
};
